import Head from 'next/head';
import { generateNextSeo } from 'next-seo/pages';

import { FaktenFuchsBadge } from '#components/FaktenFuchsBadge';
import { useEnvironment } from '#cutils/context/EnvironmentContext';
import { buildBoardSeoConfig } from '#cutils/seo/buildBoardSeoConfig';
import { TrackEventDataType } from '#types/tracking';
import { PageView, Tracktor } from 'react-tracktor';

import type { BoardData } from '../../../pages/[[...board]]';
import { SchemaOrg } from '../SchemaOrg';

import { BoardSection } from './BoardSection';
import { BoardSectionItemRichText } from './Section/Items/BoardSectionItemRichText';

type Props = {
	board: BoardData;
};

export function Board({ board }: Props) {
	const {
		constants: { SELF_ORIGIN },
	} = useEnvironment();

	const mappedBoardSections = board.sections.map((boardSection, index) => {
		return (
			<Tracktor
				eventData={{ data: { type: TrackEventDataType.BoardSectionVisible } }}
				key={boardSection.rowId}
				trackingData={{
					data: { sectionPlacement: index },
					event: { type: 'PublisherEvent' },
				}}
			>
				{({ intersectionRef }) => <BoardSection ref={intersectionRef} boardSection={boardSection} />}
			</Tracktor>
		);
	});

	const seoConfig = buildBoardSeoConfig(board, SELF_ORIGIN);

	return (
		<>
			<Head>{generateNextSeo(seoConfig)}</Head>

			{/* Injects script tags with structured data. */}
			<SchemaOrg schema={board.structuredData.schema} />
			<SchemaOrg schema={board.structuredData.boardStructuredSchema} />

			<h1 className="srOnly">{board.title}</h1>

			{!board.hasHero && <BoardSectionItemRichText introduction={board.introduction} />}

			<PageView
				key={board.shareUrl}
				pageViewData={{ data: board.trackingInfos || {}, event: { page: board.shareUrl, type: 'PageViewEvent' } }}
			/>

			<Tracktor trackingData={{ data: board.trackingInfos || {} }}>{mappedBoardSections}</Tracktor>

			<FaktenFuchsBadge boardRowId={board.rowId} />
		</>
	);
}
